<template>
  <div class="profile-task-page">
    <header ref="profileTaskFixedBar" class="profile-task-page__fixed-bar">
      <div class="profile-task-page__top-bar">
        <div class="profile-task-page__top-left">
          <button type="button" class="profile-task-page__back" aria-label="返回" @click="goBack">
            <i class="el-icon-arrow-left"></i>
          </button>
          <h1 class="profile-task-page__title">分析任务</h1>
        </div>
      </div>

      <section class="tags-strip">
        <div class="tags-strip__scroll">
          <button
            v-for="item in filterOptions"
            :key="item.value"
            type="button"
            class="tag-btn"
            :class="{ active: activeFilter === item.value }"
            @click="changeFilter(item.value)"
          >
            {{ item.label }}
          </button>
        </div>
      </section>
    </header>

    <div
      class="profile-task-page__fixed-spacer"
      :style="{ height: fixedBarHeight + 'px' }"
      aria-hidden="true"
    />

    <section class="list-wrap">
      <article
        v-for="task in tasks"
        :key="task.taskId"
        class="summary-card"
        @click="openTask(task)"
      >
        <div class="card-top">
          <span class="badge" :class="`badge--${statusTone(task.status)}`">
            {{ statusLabel(task.status) }}
          </span>
          <span class="date">{{ formatDate(task.createDate) }}</span>
        </div>
        <h3>{{ task.theme || task.inputPreview || '视频分析任务' }}</h3>
        <div v-if="task.shortUrl" class="card-content card-content--url">
          {{ task.inputPreview || task.shortUrl }}
        </div>
        <div
          v-if="task.status === 'PROCESSING' || task.status === 'PENDING' || task.status === 'QUEUED' || task.status === 'SUMMARIZING'"
          class="card-foot"
        >
          <span><i class="el-icon-loading"></i> 分析进度 {{ task.progress != null ? task.progress : 0 }}%</span>
        </div>
        <div v-else-if="task.status === 'FAILED' && task.errorMessage" class="card-foot card-foot--error">
          <span>{{ task.errorMessage }}</span>
        </div>
      </article>

      <div v-if="loading" class="list-status list-status--loading">
        <i class="el-icon-loading"></i>
        <span>加载中...</span>
      </div>
      <div v-else-if="!tasks.length" class="list-status empty-text">暂无数据</div>
      <div v-else-if="!hasMore" class="list-status no-more-text">没有更多了</div>
      <div v-if="loadingMore" class="list-status list-status--loading list-status--more">
        <i class="el-icon-loading"></i>
        <span>正在加载更多...</span>
      </div>
      <div ref="loadMoreTrigger" class="load-trigger"></div>
    </section>
  </div>
</template>

<script>
import { getApiErrorMessage } from '@/utils/apiErrorMessage'
import {
  fetchAnalysisTaskList,
  getTaskStatusLabel,
  getTaskStatusTone
} from '@/api/analysisTask'

export default {
  name: 'ProfileTaskList',
  data() {
    return {
      loading: false,
      loadingMore: false,
      isRequesting: false,
      tasks: [],
      pageNum: 1,
      pageSize: 20,
      hasMore: true,
      activeFilter: 'all',
      observer: null,
      fixedBarHeight: 118,
      filterOptions: [
        { label: '全部', value: 'all' },
        { label: '已完成', value: 'COMPLETED' },
        { label: '进行中', value: 'PENDING,PROCESSING,QUEUED,SUMMARIZING' },
        { label: '失败', value: 'FAILED' }
      ]
    }
  },
  mounted() {
    this.loadTasks(false)
    this.initObserver()
    this.setupFixedBarMeasure()
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
    if (this._fixedBarRO) {
      this._fixedBarRO.disconnect()
      this._fixedBarRO = null
    }
  },
  methods: {
    measureFixedBarHeight() {
      const el = this.$refs.profileTaskFixedBar
      if (!el) return
      const h = Math.ceil(el.getBoundingClientRect().height)
      if (h > 0) this.fixedBarHeight = h
    },
    setupFixedBarMeasure() {
      this.$nextTick(() => {
        this.measureFixedBarHeight()
        const el = this.$refs.profileTaskFixedBar
        if (!el || typeof ResizeObserver === 'undefined') return
        if (this._fixedBarRO) this._fixedBarRO.disconnect()
        this._fixedBarRO = new ResizeObserver(() => this.measureFixedBarHeight())
        this._fixedBarRO.observe(el)
      })
    },
    goBack() {
      if (window.history.length > 1) {
        this.$router.back()
      } else {
        this.$router.replace('/profile')
      }
    },
    statusLabel(status) {
      return getTaskStatusLabel(status)
    },
    statusTone(status) {
      return getTaskStatusTone(status)
    },
    formatDate(raw) {
      if (!raw) return ''
      const d = new Date(String(raw).replace(/-/g, '/'))
      if (Number.isNaN(d.getTime())) return String(raw).slice(0, 10)
      return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`
    },
    changeFilter(value) {
      if (this.activeFilter === value) return
      this.activeFilter = value
      this.loadTasks(false)
    },
    async loadTasks(isLoadMore) {
      if (this.isRequesting) return
      if (isLoadMore && (!this.hasMore || this.loadingMore)) return

      if (isLoadMore) {
        this.loadingMore = true
      } else {
        this.loading = true
        this.pageNum = 1
        this.hasMore = true
        this.tasks = []
      }

      this.isRequesting = true
      const currentPage = this.pageNum
      try {
        const status = this.activeFilter === 'all' ? '' : this.activeFilter
        const data = await fetchAnalysisTaskList({
          pageNum: currentPage,
          pageSize: this.pageSize,
          status
        })
        const records = Array.isArray(data.records) ? data.records : []
        this.tasks = isLoadMore ? [...this.tasks, ...records] : records
        this.pageNum = currentPage + 1
        this.hasMore = records.length >= this.pageSize
        if (typeof data.total === 'number') {
          this.hasMore = data.total > currentPage * this.pageSize
        }
      } catch (error) {
        console.error('加载任务列表失败:', error)
        this.$message.error(getApiErrorMessage(error, '加载任务列表失败'))
      } finally {
        this.loading = false
        this.loadingMore = false
        this.isRequesting = false
      }
    },
    initObserver() {
      if (!window.IntersectionObserver) return
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && this.hasMore && !this.isRequesting) {
              this.loadTasks(true)
            }
          })
        },
        { root: null, rootMargin: '600px', threshold: 0.01 }
      )
      this.$nextTick(() => {
        if (this.$refs.loadMoreTrigger) {
          this.observer.observe(this.$refs.loadMoreTrigger)
        }
      })
    },
    openTask(task) {
      const status = task && task.status != null ? String(task.status).trim().toUpperCase() : ''
      if (status === 'COMPLETED' && task.summaryId) {
        this.$router.push({
          path: '/summary-list/detail',
          query: {
            from: 'profile-tasks',
            id: String(task.summaryId)
          }
        })
        return
      }
      if (status === 'PROCESSING' || status === 'PENDING' || status === 'QUEUED' || status === 'SUMMARIZING') {
        this.$message.info('任务分析中，请稍后再查看')
        return
      }
      if (status === 'FAILED') {
        this.$message.warning(task.errorMessage || '任务分析失败')
        return
      }
      this.$message.info('暂无可查看的总结内容')
    }
  }
}
</script>

<style scoped>
.profile-task-page {
  min-height: 100%;
  background: #fff;
  padding: 0 0 80px;
  box-sizing: border-box;
  -webkit-tap-highlight-color: transparent;
}

.profile-task-page__fixed-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 40;
  box-sizing: border-box;
  background: #fff;
  border-bottom: 1px solid rgba(7, 29, 73, 0.08);
  transform: translateZ(0);
  backface-visibility: hidden;
}

.profile-task-page__fixed-spacer {
  width: 100%;
  flex-shrink: 0;
  pointer-events: none;
}

.profile-task-page__top-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  gap: 12px;
  padding: 16px 20px 12px;
  padding-top: calc(16px + env(safe-area-inset-top, 0px));
  padding-left: calc(12px + env(safe-area-inset-left, 0px));
  padding-right: calc(20px + env(safe-area-inset-right, 0px));
  background: #fff;
}

.profile-task-page__top-left {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  flex: 1;
}

.profile-task-page__back {
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  padding: 0;
  margin: 0 4px 0 -4px;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: rgba(0, 0, 0, 0.75);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s, color 0.2s;
}

.profile-task-page__back:hover {
  background: rgba(29, 93, 155, 0.08);
  color: #1d5d9b;
}

.profile-task-page__back i {
  font-size: 20px;
}

.profile-task-page__title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.88);
  letter-spacing: -0.02em;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tags-strip {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 0;
  padding: 0 16px 12px;
}

.profile-task-page__fixed-bar .tags-strip {
  padding-bottom: 12px;
}

.tags-strip__scroll {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-wrap: nowrap;
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 2px;
  -webkit-overflow-scrolling: touch;
}

.tags-strip__scroll::-webkit-scrollbar {
  display: none;
}

.tag-btn {
  border: none;
  background: #f5f9ff;
  color: #2c4774;
  border-radius: 18px;
  padding: 7px 16px;
  white-space: nowrap;
  font-size: 14px;
  flex-shrink: 0;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.tag-btn.active {
  background: #4253ff;
  color: #fff;
}

.list-wrap {
  width: 100%;
  box-sizing: border-box;
  padding: 0 16px;
}

.summary-card {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #eef3ff;
  border-radius: 16px;
  padding: 14px;
  margin: 0 0 12px;
  box-shadow: 0 2px 8px rgba(66, 83, 255, 0.05);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
  outline: none;
  transition: background 0.15s ease;
}

.summary-card:active {
  background: #f8faff;
}

.card-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.badge {
  display: inline-flex;
  align-items: center;
  height: 22px;
  border-radius: 8px;
  background: #4253ff;
  color: #ffffff;
  padding: 0 8px;
  font-size: 12px;
}

.badge--success {
  background: #22c55e;
}

.badge--warning {
  background: #f59e0b;
}

.badge--danger {
  background: #ef4444;
}

.badge--info {
  background: #64748b;
}

.date {
  color: #95a2bf;
  font-size: 12px;
}

.summary-card h3 {
  margin: 0 0 8px;
  color: #2f47ff;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.45;
  word-break: break-word;
}

.card-content {
  color: #4f6188;
  font-size: 16px;
  line-height: 1.5;
  min-height: 0;
}

.card-content--url {
  font-size: 13px;
  color: #8d9bb8;
  word-break: break-all;
}

.card-foot {
  margin-top: 12px;
  border-top: 1px solid #f0f3fa;
  padding-top: 10px;
  color: #8d9bb8;
  font-size: 13px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.card-foot--error {
  color: #ef4444;
}

.list-status {
  text-align: center;
  color: #98a5c0;
  padding: 12px 0;
  font-size: 13px;
}

.list-status--loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #5e73a8;
}

.list-status--loading .el-icon-loading {
  font-size: 18px;
  animation: rotating 1.2s linear infinite;
}

.list-status--more {
  padding: 16px 0 8px;
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.load-trigger {
  height: 1px;
}

@media (max-width: 480px) {
  .profile-task-page__title {
    font-size: 18px;
  }
}
</style>
